/**
 * (owner, Sep 30: "add explosives: c4, frag grenade, sticky bomb, smoke grenade, flashbang, booby trap") A charge is an explosive out in
 * the world: a frag grenade on the ground with its fuse burning, a sticky bomb on the car it hit, C4 set down waiting for its owner's
 * R, a booby trap armed on the pavement, a smoke or flash grenade about to pop. What a weapon becomes is its content's `charge`
 * (types.ts): thrown ones fly as a projectile first and land or stick (game.ts updateProjectiles → `landCharge`), placed ones go
 * down at the thrower's feet (`launchCharge`). `stepCharges` carries the stuck ones along and sets them off; smoke is a lasting
 * cloud nobody sees through (`smokeBetween`), a flash stuns the people inside it (`Person.stunUntil`) and whites out a player's screen.
 */
import type { Weapon } from '../content/types';
import { forward, newId, type Person, type Projectile } from './entities';
import type { Game } from './game';

export interface Charge {
  id: number; def: Weapon; ownerId: number;
  x: number; y: number; z: number;
  /** seconds since it landed / was set */
  t: number;
  /** stuck to a vehicle or a person: its id and where on it (world offset, turned with it) */
  stuck?: { v?: number; p?: number; ox: number; oy: number; a0: number };
}
/** how many one person may have out at once (C4 and traps) */
export const CHARGE_CAP = 8;
const TRAP_ARM = 2, TRAP_REACH = 1.0;

/** fire with a charge in hand: a placed one goes down at your feet (on a car beside you, if it sticks); a thrown one flies (game.fireWeapon) */
export function launchCharge(game: Game, p: Person, def: Weapon): boolean {
  const c = def.charge!;
  if (!c.place) return false; // (thrown: the projectile, landing through landCharge)
  if (game.charges.filter((k) => k.ownerId === p.id).length >= CHARGE_CAP) { game.say(`Eight out already — set some off first`, p); return true; }
  const [fx, fy] = forward(p.angle), x = p.x + fx * 0.6, y = p.y + fy * 0.6, z = game.personBase(p);
  const k: Charge = { id: newId(), def, ownerId: p.id, x, y, z, t: 0 };
  if (c.stick) { const v = game.vehicles.find((q) => !q.wrecked && Math.hypot(q.x - x, q.y - y) < Math.max(q.def.size[0], q.def.size[1]) / 2 + 0.8); if (v) k.stuck = { v: v.id, ox: x - v.x, oy: y - v.y, a0: v.angle }; }
  game.charges.push(k); game.events.push({ kind: 'gear', x, y, cls: 'charge_set' });
  game.say(c.remote ? `${def.name} set${k.stuck ? ' on the car' : ''} — R with it in hand sets them off` : c.trap ? `${def.name} set — armed in two seconds` : `${def.name} set`, p);
  return true;
}
/** a thrown charge's flight ended: on what it hit when it sticks, else on the ground where it came down */
export function landCharge(game: Game, pr: Projectile, hitV?: number, hitP?: number): void {
  const def = pr.charge ? game.reg.get('weapon', pr.charge) : undefined; if (!def?.charge) return;
  const k: Charge = { id: newId(), def, ownerId: pr.ownerId, x: pr.x, y: pr.y, z: Math.max(0, pr.z - 1.1), t: 0 };
  if (def.charge.stick && hitV !== undefined) { const v = game.vehicles.find((q) => q.id === hitV); if (v) { k.stuck = { v: v.id, ox: pr.x - v.x, oy: pr.y - v.y, a0: v.angle }; k.z = v.alt; } }
  else if (def.charge.stick && hitP !== undefined) { const q = game.people.find((x) => x.id === hitP); if (q) { k.stuck = { p: q.id, ox: pr.x - q.x, oy: pr.y - q.y, a0: q.angle }; } }
  game.charges.push(k); game.events.push({ kind: 'gear', x: k.x, y: k.y, cls: k.stuck ? 'charge_set' : 'charge_land' }); // (a grenade's clink tells you it is at your feet)
}
/** R with a remote charge in hand: every one of yours goes off */
export function detonate(game: Game, p: Person): number {
  const mine = game.charges.filter((k) => k.ownerId === p.id && k.def.charge?.remote);
  for (const k of mine) fire(game, k);
  game.charges = game.charges.filter((k) => !mine.includes(k));
  if (mine.length) game.events.push({ kind: 'gear', x: p.x, y: p.y, cls: 'detonator' });
  return mine.length;
}
function fire(game: Game, k: Charge): void {
  const c = k.def.charge!;
  if (c.effect === 'smoke') { game.effects.push({ x: k.x, y: k.y, z: k.z, r: c.radius ?? 6, t: 0, life: c.duration ?? 15, kind: 'cloud' }); game.events.push({ kind: 'gear', x: k.x, y: k.y, cls: 'smoke_pop' }); return; }
  if (c.effect === 'flash') {
    const r = c.radius ?? 9, until = game.time + (c.duration ?? 4);
    for (const q of game.people) { if (q.dead || q.vehicle || Math.hypot(q.x - k.x, q.y - k.y) > r) continue; if (!q.isPlayer) q.stunUntil = until; }
    game.effects.push({ x: k.x, y: k.y, z: k.z, r, t: 0, life: 0.35, kind: 'flash' });
    game.events.push({ kind: 'flashbang', x: k.x, y: k.y, strength: r, pitch: c.duration ?? 4 });
    return;
  }
  game.blast(k.x, k.y, k.z + 0.5, k.def.splash ?? 3, k.def.damage, k.ownerId);
}
/** a step: the stuck ones ride along, the fuses burn, the traps watch */
export function stepCharges(game: Game, dt: number): void {
  if (!game.charges.length) return;
  const gone = new Set<Charge>();
  for (const k of game.charges) {
    k.t += dt;
    if (k.stuck) {
      const host = k.stuck.v !== undefined ? game.vehicles.find((v) => v.id === k.stuck!.v) : game.people.find((q) => q.id === k.stuck!.p);
      if (!host || ('dead' in host && host.dead)) k.stuck = undefined;
      else { const da = ('angle' in host ? host.angle : 0) - k.stuck.a0, c = Math.cos(da), s = Math.sin(da); k.x = host.x + k.stuck.ox * c - k.stuck.oy * s; k.y = host.y + k.stuck.ox * s + k.stuck.oy * c; k.z = 'alt' in host ? host.alt : game.personBase(host as Person); }
    }
    const c = k.def.charge!;
    if (c.fuse !== undefined && !c.remote && !c.trap && k.t >= c.fuse) { fire(game, k); gone.add(k); continue; }
    if (c.trap && k.t >= TRAP_ARM) {
      const tripped = game.people.some((q) => q.id !== k.ownerId && !q.dead && !q.vehicle && Math.abs(game.personBase(q) - k.z) < 1.5 && Math.hypot(q.x - k.x, q.y - k.y) < TRAP_REACH)
        || game.vehicles.some((v) => v.driver && v.driver.id !== k.ownerId && v.alt < 1 && Math.hypot(v.x - k.x, v.y - k.y) < Math.max(v.def.size[0], v.def.size[1]) / 2);
      if (tripped) { fire(game, k); gone.add(k); }
    }
  }
  if (gone.size) game.charges = game.charges.filter((k) => !gone.has(k));
}
/** (smoke) is the line from a to b through a smoke cloud — nobody sees through one, nobody inside one is seen */
export function smokeBetween(game: Pick<Game, 'effects'>, ax: number, ay: number, bx: number, by: number): boolean {
  for (const e of game.effects) {
    if (e.kind !== 'cloud') continue;
    const r = e.r * Math.min(1, (e.life - e.t) / 3 + 0.3); // (thinning in its last seconds)
    const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy || 1, u = Math.max(0, Math.min(1, ((e.x - ax) * dx + (e.y - ay) * dy) / L2));
    if (Math.hypot(ax + dx * u - e.x, ay + dy * u - e.y) < r) return true;
  }
  return false;
}
